import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import "./UserTaskList.css";

const PAGE_SIZE = 15;

const UserTaskList = () => {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserTasks();
  }, []);

  // Re-filter whenever search or users change
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      setFiltered(users);
    } else {
      setFiltered(
        users.filter(
          (u) =>
            u.name?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q) ||
            u.mobile?.toString().includes(q)
        )
      );
    }
    setPage(1);
  }, [search, users]);

  const fetchUserTasks = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/users/tasks");
      setUsers(res.data.data || []);
    } catch (err) {
      console.error("❌ Error fetching user tasks:", err);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="utl-container">
      {/* Header */}
      <div className="utl-header">
        <h1 className="utl-title">Manage User Tasks</h1>
        <input
          className="utl-search"
          placeholder="🔍 Search by name, email or mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="utl-loading">
          <div className="utl-spinner" />
          <p>Loading users...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="utl-empty">No users found</div>
      ) : (
        <>
          <div className="utl-meta">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} users
          </div>

          <div className="utl-table-wrap">
            <table className="utl-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Total</th>
                  <th>✅ Done</th>
                  <th>⏳ Pending</th>
                  <th>❌ Missed</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageData.map((user, i) => {
                  const completed = user.tasks.filter((t) => t.status === "completed").length;
                  const incomplete = user.tasks.filter((t) => t.status !== "completed" && t.status !== "missed").length;
                  const missed = user.missedTasks?.length || 0;
                  const total = user.tasks.length + missed;

                  return (
                    <tr key={user.userId}>
                      <td className="utl-num">{(page - 1) * PAGE_SIZE + i + 1}</td>
                      <td className="utl-name">{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.mobile}</td>
                      <td><span className="utl-badge utl-badge-total">{total}</span></td>
                      <td><span className="utl-badge utl-badge-done">{completed}</span></td>
                      <td><span className="utl-badge utl-badge-pending">{incomplete}</span></td>
                      <td><span className="utl-badge utl-badge-missed">{missed}</span></td>
                      <td>
                        <button
                          className="utl-view-btn"
                          onClick={() => navigate(`/usertasks/${user.userId}`)}
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="utl-pagination">
              <button className="utl-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === '...'
                    ? <span key={`e${idx}`} className="utl-ellipsis">…</span>
                    : <button key={p} className={`utl-page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                )}
              <button className="utl-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserTaskList;